import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { SHOW_PRICING } from '../App';
import { 
  Sparkles, Eye, Palette, Instagram, FileText, TrendingUp, 
  Users, Globe, ArrowRight, CheckCircle, Zap, Target, Star
} from 'lucide-react';

const Introduction = () => {
  const navigate = useNavigate();

  const tools = [
    { icon: Eye, name: 'AI Brand Audit', desc: 'Analyze your current brand positioning' },
    { icon: Palette, name: 'Vision Board Studio', desc: 'Create visual direction boards' },
    { icon: FileText, name: 'Clierxter Content Strategy', desc: '3 posts/week strategic framework' },
    { icon: TrendingUp, name: 'AI Brand Glow Up', desc: 'Before/after transformation' },
    { icon: Instagram, name: 'Content Planning Studio', desc: 'Calendar, planner, feed preview' },
    { icon: Zap, name: 'Online Income Lab', desc: 'Monetization strategies' },
    { icon: Sparkles, name: 'AI Brand Studio', desc: 'Complete brand generation' }
  ];

  const beforeAfter = {
    before: [
      'Unclear brand identity',
      'Inconsistent content',
      'Lack of strategy',
      'Limited visibility'
    ],
    after: [
      'Clear vision & identity',
      'Strong brand presence',
      'Strategic content plan',
      'Growing influence'
    ]
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#1C1C1C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="group" data-testid="intro-logo">
            <span className="font-serif text-xl text-white tracking-wide">Clierxter</span>
            <span className="block text-[#D4AF37] text-[9px] font-accent tracking-[0.3em]">VISION LAB</span>
          </Link>
          <div className="flex items-center gap-6">
            {SHOW_PRICING && (
              <Link to="/pricing" className="text-[#A1A1AA] hover:text-white text-sm transition-colors">
                Pricing
              </Link>
            )}
            <Button onClick={() => navigate('/auth')} className="btn-gold px-6 py-2.5 text-sm" data-testid="intro-cta-nav">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
              <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.3em]">THE MANUELA CLIERXTER METHOD</span>
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            </div>

            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-8">
              Build the Brand of<br />
              <span className="text-[#E5C585]">Your Future Self</span>
            </h1>

            <p className="text-[#A1A1AA] text-lg md:text-xl max-w-3xl mx-auto mb-12 font-light leading-relaxed">
              Clierxter Vision Lab is an AI-powered platform designed to help women entrepreneurs 
              clarify their vision, build a powerful brand and transform their ideas into 
              <span className="text-[#E5C585]"> visibility</span>, 
              <span className="text-[#E5C585]"> influence</span> and 
              <span className="text-[#E5C585]"> income</span>.
            </p>

            <Button 
              onClick={() => navigate('/auth')} 
              className="btn-gold px-12 py-5 text-lg flex items-center gap-3 mx-auto"
              data-testid="hero-cta"
            >
              Start Your Transformation
              <ArrowRight size={20} />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* About Manuela Section */}
      <section className="py-24 px-6 lg:px-12 border-t border-[#1C1C1C]">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <div className="relative">
              <div className="aspect-[4/5] relative">
                <img 
                  src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&h=750&fit=crop"
                  alt="Manuela Clierxter"
                  className="w-full h-full object-cover border border-[#27272A]"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#050505] to-transparent h-32" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="font-serif text-2xl text-white">Manuela Clierxter</p>
                  <p className="text-[#D4AF37] text-xs font-accent tracking-[0.2em] mt-1">VISUAL STRATEGIST & FOUNDER</p>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-gradient-radial from-[#D4AF37]/20 to-transparent blur-2xl" />
            </div>

            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">THE VISIONARY BEHIND</span>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
                About Manuela Clierxter
              </h2>

              <div className="space-y-6 text-[#A1A1AA] font-light leading-relaxed">
                <p>
                  Clierxter Vision Lab was created by <span className="text-white">Manuela Clierxter</span>, 
                  visual strategist and entrepreneur.
                </p>
                <p>
                  After working with many entrepreneurs and witnessing their struggles to bring clarity 
                  to their vision and brand, Manuela created a platform where 
                  <span className="text-[#E5C585]"> vision</span>, 
                  <span className="text-[#E5C585]"> branding</span>, 
                  <span className="text-[#E5C585]"> community</span> and 
                  <span className="text-[#E5C585]"> strategy</span> come together.
                </p>
                <p className="text-white font-serif text-lg italic border-l-2 border-[#D4AF37] pl-6">
                  "Your vision deserves to become a powerful brand."
                </p>
                <p>
                  Clierxter Vision Lab helps women move from confusion to clarity, 
                  from ideas to identity, and from identity to income.
                </p>
                <p className="text-[#E5C585] font-serif text-xl">
                  Embody your vision.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Platform Tools Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#0A0A0A] border-t border-b border-[#1C1C1C]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-8 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
              <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">POWERFUL TOOLS</span>
              <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
              Inside Clierxter Vision Lab
            </h2>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto font-light">
              AI-powered tools designed to help you build a clear, powerful, and profitable brand.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <motion.div
                  key={tool.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group p-8 bg-[#050505] border border-[#1C1C1C] hover:border-[#D4AF37]/30 transition-all"
                >
                  <div className="w-14 h-14 flex items-center justify-center border border-[#27272A] group-hover:border-[#D4AF37]/40 transition-colors mb-6">
                    <Icon className="text-[#E5C585]" size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl text-white mb-3 group-hover:text-[#E5C585] transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-[#52525B] text-sm font-light">{tool.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
                <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">WOMEN CONNECT</span>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
                Clierxter Global Women Community
              </h2>

              <div className="space-y-6">
                {[
                  { icon: Globe, text: 'Connect with women entrepreneurs worldwide' },
                  { icon: Users, text: 'Collaborate and network with like-minded visionaries' },
                  { icon: Star, text: 'Share progress and celebrate wins together' },
                  { icon: Target, text: 'Promote your business in the directory (Pro)' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center gap-4"
                    >
                      <div className="w-12 h-12 flex items-center justify-center border border-[#27272A]">
                        <Icon className="text-[#D4AF37]" size={20} />
                      </div>
                      <p className="text-[#A1A1AA] font-light">{item.text}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=300&h=400&fit=crop"
                  alt="Community"
                  className="w-full aspect-[3/4] object-cover border border-[#27272A]"
                />
                <img 
                  src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=300&h=400&fit=crop"
                  alt="Networking"
                  className="w-full aspect-[3/4] object-cover border border-[#27272A] mt-8"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-gradient-radial from-[#D4AF37]/10 to-transparent blur-3xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#0A0A0A] border-t border-b border-[#1C1C1C]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-8 h-px bg-gradient-to-r from-transparent to-[#D4AF37]" />
              <span className="font-accent text-[#D4AF37] text-[10px] tracking-[0.25em]">YOUR TRANSFORMATION</span>
              <div className="w-8 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              Before & After Clierxter
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Before */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-[#050505] border border-red-500/20"
            >
              <h3 className="font-accent text-red-400/80 text-xs tracking-[0.2em] mb-6">BEFORE</h3>
              <div className="space-y-4">
                {beforeAfter.before.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-2 h-2 bg-red-500/50" />
                    <p className="text-[#71717A]">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* After */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 bg-[#050505] border border-[#D4AF37]/30"
            >
              <h3 className="font-accent text-[#D4AF37] text-xs tracking-[0.2em] mb-6">AFTER</h3>
              <div className="space-y-4">
                {beforeAfter.after.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <CheckCircle className="text-[#D4AF37]" size={16} />
                    <p className="text-white">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-[#D4AF37]/5 via-transparent to-transparent" />
        
        <div className="max-w-4xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-8">
              Your Vision Deserves More<br />Than Just Ideas
            </h2>

            <div className="space-y-2 text-[#A1A1AA] text-lg mb-12 font-light">
              <p>It deserves <span className="text-[#E5C585]">clarity</span>.</p>
              <p>It deserves <span className="text-[#E5C585]">strategy</span>.</p>
              <p>It deserves to become a <span className="text-[#E5C585]">powerful brand</span>.</p>
            </div>

            <Button 
              onClick={() => navigate('/auth')} 
              className="btn-gold px-12 py-5 text-lg flex items-center gap-3 mx-auto"
              data-testid="final-cta"
            >
              Choose Your Plan
              <ArrowRight size={20} />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 border-t border-[#1C1C1C]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <span className="font-serif text-lg text-white">Clierxter</span>
            <span className="text-[#D4AF37] text-[9px] font-accent tracking-[0.2em] ml-2">VISION LAB</span>
          </div>
          <p className="text-[#52525B] text-sm">
            © 2026 Clierxter Vision Lab. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Introduction;
